<template>
  <div>
    <!-- Video Showcase Section -->
    <section class="relative md:py-24 py-16 bg-gray-50 dark:bg-slate-800 overflow-hidden" id="video-showcase">

      <!-- Decorative blobs -->
      <div class="absolute -top-20 right-0 w-80 h-80 bg-orange-100 dark:bg-orange-900/10 rounded-full blur-3xl opacity-40 pointer-events-none"></div>

      <div class="container relative z-10">
        <!-- Header -->
        <div class="grid grid-cols-1 pb-10 text-center video-header">
          <div class="inline-flex items-center gap-2 justify-center mb-3">
            <div class="h-px w-8 bg-orange-600"></div>
            <h6 class="text-orange-600 text-sm font-semibold uppercase tracking-widest">Sultan Flowers Extract Palace</h6>
            <div class="h-px w-8 bg-orange-600"></div>
          </div>
          <h3 class="mb-4 md:text-4xl text-3xl font-bold dark:text-white leading-tight">
            Discover the <span class="text-orange-600">Fragrance Experience</span>
          </h3>
          <p class="text-slate-400 dark:text-slate-300 max-w-xl mx-auto leading-relaxed">
            Welcome to
            <span class="font-semibold text-orange-600">Sultan Flowers Extract Palace</span>.
            Explore our video gallery and see how we create our raw perfume extracts, essential oils, handmade Pyrex bottles, and healing aroma blends.
          </p>
        </div>

        <!-- Video Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-4 md:grid-cols-2 gap-6 mt-4">
          <div
            v-for="(n, idx) in videos"
            :key="idx"
            class="video-card"
            :style="{ transitionDelay: `${idx * 0.12}s` }"
          >
            <div class="video-frame-wrap relative rounded-2xl overflow-hidden shadow-lg group">
              <iframe
                :src="n.url"
                class="w-full h-56"
                allowfullscreen
                loading="lazy"
              ></iframe>
              <!-- Hover shimmer overlay -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section
      class="py-24 w-full table relative bg-center bg-cover overflow-hidden"
      :style="{ backgroundImage: `url(${bgcta})` }"
    >
      <div class="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90"></div>

      <!-- Animated lines -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div v-for="i in 4" :key="i"
             class="cta-line absolute h-px w-full bg-gradient-to-r from-transparent via-orange-600/30 to-transparent"
             :style="{ top: `${i * 25}%`, animationDelay: `${i * 0.5}s` }">
        </div>
      </div>

      <div class="container relative z-10">
        <div class="grid grid-cols-1 text-center cta-content">
          <h3 class="mb-6 md:text-4xl text-3xl text-white font-bold leading-tight">
            Visit Sultan Extract<br><span class="text-orange-400">Flower Palace</span>
          </h3>
          <p class="text-white/70 max-w-lg mx-auto mb-10 leading-relaxed">
            From pure raw perfume extracts to authentic oriental essences — explore and experience scents in their most natural form.
          </p>
          <div class="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.youtube.com/@yourchannel"
              target="_blank"
              rel="noopener noreferrer"
              class="cta-btn-primary group inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-600/40"
            >
              <svg class="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-2.75 12 12 0 01-1.52-4.46A12.06 12.06 0 0112 0a12 12 0 01-2.3 3.48 4.83 4.83 0 01-3.77 2.75A12 12 0 010 12a12 12 0 016.23 5.56 4.83 4.83 0 013.77 2.75A12 12 0 0112 24a12 12 0 012.3-3.48 4.83 4.83 0 013.77-2.75A12 12 0 0124 12a12 12 0 01-4.41-5.31z"/>
              </svg>
              Watch on YouTube
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import bgcta from '../assets/images/bg/ctaa.png'
import { computed, onMounted } from "vue";
import store from "@/store";
import { useScrollReveal } from '@/composables/useScrollReveal';

const videos = computed(() => store.state.videos.data)

// Re-scans when the videos arrive so late-rendered cards still reveal
useScrollReveal('.video-header, .video-card, .cta-content', () => videos.value)

onMounted(() => {
  store.dispatch('getVideos')
});
</script>

<style scoped>
.video-header {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.video-header.revealed {
  opacity: 1;
  transform: translateY(0);
}

.video-card {
  opacity: 0;
  transform: translateY(40px) scale(0.95);
  transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.video-card.revealed {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.video-card:hover .video-frame-wrap {
  transform: translateY(-4px);
  box-shadow: 0 20px 50px rgba(234, 88, 12, 0.15);
}
.video-frame-wrap {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s ease;
}

.cta-content {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.cta-content.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* CTA animated lines */
@keyframes slideLine {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
.cta-line {
  animation: slideLine 4s linear infinite;
}
</style>
